import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Stack,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    Divider,
    Chip,
    Button,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DataGridPro, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import type { CollectionStandings } from '@moment-golf/types';

// ─── Local types that mirror the server response ─────────────────────────────

interface ActiveEventSummaryEntry {
    eventID: string;
    eventName: string;
    individualPoints: Array<{ playerName: string; points: number }>;
    pairPoints: Array<{ pairName: string; points: number }>;
    teamPoints: Array<{ teamName: string; points: number }>;
}

export interface StandingsPreviewData {
    referenceStandings: CollectionStandings | null;
    activeEventSummaries: ActiveEventSummaryEntry[];
    projectedStandings: CollectionStandings;
}

interface StandingsPreviewDialogProps {
    open: boolean;
    onClose: () => void;
    previewData: StandingsPreviewData;
    factor: number;
}

// ─── Shared helpers ───────────────────────────────────────────────────────────

function gridSx(factor: number) {
    return {
        fontSize: 12 * factor,
        border: 'none',
        backgroundColor: 'sepia.background',
        '& .MuiDataGrid-main': { backgroundColor: 'sepia.background' },
        '& .MuiDataGrid-virtualScroller': { overscrollBehavior: 'none', backgroundColor: 'sepia.background' },
        '& .MuiDataGrid-columnHeader': { backgroundColor: 'sepia.background' },
        '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600, fontSize: 12 * factor },
    };
}

const WrapCell = (params: GridRenderCellParams) => (
    <Box sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.35, py: 0.75 }}>
        {params.value}
    </Box>
);

const DeltaCell = (params: GridRenderCellParams) => {
    const delta = params.value as number | undefined;
    if (delta == null || delta === 0) return <Box sx={{ py: 0.75, color: 'text.disabled' }}>–</Box>;
    return (
        <Box sx={{ py: 0.75, color: delta > 0 ? 'success.main' : 'text.primary', fontWeight: 600 }}>
            {delta > 0 ? `+${delta}` : delta}
        </Box>
    );
};

// ─── Section header ───────────────────────────────────────────────────────────

const SectionHeader = ({ label, subtitle, factor }: { label: string; subtitle?: string; factor: number }) => (
    <Stack spacing={0.25} sx={{ pb: 1 }}>
        <Typography variant='subtitle2' sx={{ fontSize: 12 * factor, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'text.secondary' }}>
            {label}
        </Typography>
        {subtitle && (
            <Typography variant='caption' sx={{ fontSize: 10 * factor, color: 'text.disabled' }}>
                {subtitle}
            </Typography>
        )}
    </Stack>
);

// ─── Reference section ────────────────────────────────────────────────────────

function ReferenceSection({
    standings,
    view,
    factor,
}: {
    standings: CollectionStandings | null;
    view: 'individual' | 'pairs' | 'teams';
    factor: number;
}) {
    if (!standings) {
        return (
            <Box sx={{ pt: 2 }}>
                <Typography variant='body2' sx={{ fontSize: 12 * factor, color: 'text.secondary' }}>
                    No standings have been calculated yet.
                </Typography>
            </Box>
        );
    }

    const throughDate = standings.calculatedThrough ?? standings.computedAt;
    const subtitle = throughDate
        ? `Through ${new Date(throughDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })}`
        : undefined;

    if (view === 'individual') {
        const rows = standings.individual.map((r, i) => ({ id: i, ...r }));
        const cols: GridColDef[] = [
            { field: 'rank', headerName: 'Rank', width: 52 * factor, type: 'number', align: 'center', headerAlign: 'center', renderCell: WrapCell },
            { field: 'playerName', headerName: 'Player', flex: 1, minWidth: 80, renderCell: WrapCell },
            { field: 'totalPoints', headerName: 'Pts', width: 64 * factor, type: 'number', align: 'right', headerAlign: 'right', renderCell: WrapCell },
            { field: 'eventsPlayed', headerName: 'Evts', width: 52 * factor, type: 'number', align: 'center', headerAlign: 'center', renderCell: WrapCell },
        ];
        return (
            <>
                <SectionHeader label='Reference' subtitle={subtitle} factor={factor} />
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    <DataGridPro rows={rows} columns={cols} density='compact' getRowHeight={() => 'auto'} disableColumnMenu disableRowSelectionOnClick hideFooter sx={{ ...gridSx(factor), width: '100%', height: '100%' }} />
                </Box>
            </>
        );
    }

    if (view === 'pairs') {
        const rows = standings.pairs.map((r, i) => ({ id: i, ...r }));
        const cols: GridColDef[] = [
            { field: 'rank', headerName: 'Rank', width: 52 * factor, type: 'number', align: 'center', headerAlign: 'center', renderCell: WrapCell },
            { field: 'pairName', headerName: 'Pair', flex: 1, minWidth: 80, renderCell: WrapCell },
            { field: 'totalPoints', headerName: 'Pts', width: 64 * factor, type: 'number', align: 'right', headerAlign: 'right', renderCell: WrapCell },
            { field: 'eventsPlayed', headerName: 'Evts', width: 52 * factor, type: 'number', align: 'center', headerAlign: 'center', renderCell: WrapCell },
        ];
        return (
            <>
                <SectionHeader label='Reference' subtitle={subtitle} factor={factor} />
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    <DataGridPro rows={rows} columns={cols} density='compact' getRowHeight={() => 'auto'} disableColumnMenu disableRowSelectionOnClick hideFooter sx={{ ...gridSx(factor), width: '100%', height: '100%' }} />
                </Box>
            </>
        );
    }

    // teams
    const rows = standings.teams.map((r, i) => ({ id: i, ...r }));
    const cols: GridColDef[] = [
        { field: 'rank', headerName: 'Rank', width: 52 * factor, type: 'number', align: 'center', headerAlign: 'center', renderCell: WrapCell },
        { field: 'teamName', headerName: 'Team', flex: 1, minWidth: 80, renderCell: WrapCell },
        { field: 'totalPoints', headerName: 'Pts', width: 64 * factor, type: 'number', align: 'right', headerAlign: 'right', renderCell: WrapCell },
        { field: 'eventsQualified', headerName: 'Evts', width: 52 * factor, type: 'number', align: 'center', headerAlign: 'center', renderCell: WrapCell },
    ];
    return (
        <>
            <SectionHeader label='Reference' subtitle={subtitle} factor={factor} />
            <Box sx={{ flex: 1, minHeight: 0 }}>
                <DataGridPro rows={rows} columns={cols} density='compact' getRowHeight={() => 'auto'} disableColumnMenu disableRowSelectionOnClick hideFooter sx={{ ...gridSx(factor), width: '100%', height: '100%' }} />
            </Box>
        </>
    );
}

// ─── Active Events section ────────────────────────────────────────────────────

function ActiveEventsSection({
    summaries,
    view,
    factor,
}: {
    summaries: ActiveEventSummaryEntry[];
    view: 'individual' | 'pairs' | 'teams';
    factor: number;
}) {
    // Flatten all active-event points into a single grid: [Player/Pair/Team, Event, Points]
    const rows: { id: number; name: string; eventName: string; points: number }[] = [];
    let idx = 0;

    for (const ev of summaries) {
        const source =
            view === 'individual' ? ev.individualPoints.map((r) => ({ name: r.playerName, points: r.points }))
            : view === 'pairs'    ? ev.pairPoints.map((r) => ({ name: r.pairName, points: r.points }))
            :                       ev.teamPoints.map((r) => ({ name: r.teamName, points: r.points }));

        for (const row of source) {
            rows.push({ id: idx++, name: row.name, eventName: ev.eventName, points: row.points });
        }
    }

    const nameHeader = view === 'individual' ? 'Player' : view === 'pairs' ? 'Pair' : 'Team';
    const cols: GridColDef[] = [
        { field: 'name', headerName: nameHeader, flex: 1, minWidth: 80, renderCell: WrapCell },
        { field: 'eventName', headerName: 'Event', flex: 1, minWidth: 80, renderCell: WrapCell },
        { field: 'points', headerName: 'Pts', width: 64 * factor, type: 'number', align: 'right', headerAlign: 'right', renderCell: WrapCell },
    ];

    const eventNames = summaries.map((s) => s.eventName);

    return (
        <>
            <SectionHeader
                label='Active Events'
                subtitle={summaries.length === 0 ? undefined : `${summaries.length} new event${summaries.length > 1 ? 's' : ''} since last calculation`}
                factor={factor}
            />
            {summaries.length > 0 && (
                <Stack direction='row' spacing={0.5} flexWrap='wrap' sx={{ pb: 1, gap: 0.5 }}>
                    {eventNames.map((name) => (
                        <Chip key={name} label={name} size='small' sx={{ fontSize: 10 * factor, height: 20 * factor }} />
                    ))}
                </Stack>
            )}
            {summaries.length === 0 ? (
                <Box sx={{ pt: 1 }}>
                    <Typography variant='body2' sx={{ fontSize: 12 * factor, color: 'text.secondary' }}>
                        No new events since last calculation.
                    </Typography>
                </Box>
            ) : rows.length === 0 ? (
                <Box sx={{ pt: 1 }}>
                    <Typography variant='body2' sx={{ fontSize: 12 * factor, color: 'text.secondary' }}>
                        No points earned in the active events for this view.
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    <DataGridPro
                        rows={rows}
                        columns={cols}
                        density='compact'
                        getRowHeight={() => 'auto'}
                        disableColumnMenu
                        disableRowSelectionOnClick
                        hideFooter={rows.length <= 100}
                        sx={{ ...gridSx(factor), width: '100%', height: '100%' }}
                    />
                </Box>
            )}
        </>
    );
}

// ─── Projected section ────────────────────────────────────────────────────────

function ProjectedSection({
    projected,
    reference,
    view,
    factor,
}: {
    projected: CollectionStandings;
    reference: CollectionStandings | null;
    view: 'individual' | 'pairs' | 'teams';
    factor: number;
}) {
    if (view === 'individual') {
        // Build a delta map from reference
        const refMap = new Map<string, number>();
        if (reference) {
            for (const r of reference.individual) refMap.set(r.playerName, r.totalPoints);
        }

        const rows = projected.individual.map((r, i) => ({
            id: i,
            ...r,
            delta: r.totalPoints - (refMap.get(r.playerName) ?? 0),
        }));
        const cols: GridColDef[] = [
            { field: 'rank', headerName: 'Rank', width: 52 * factor, type: 'number', align: 'center', headerAlign: 'center', renderCell: WrapCell },
            { field: 'playerName', headerName: 'Player', flex: 1, minWidth: 80, renderCell: WrapCell },
            { field: 'totalPoints', headerName: 'Pts', width: 64 * factor, type: 'number', align: 'right', headerAlign: 'right', renderCell: WrapCell },
            { field: 'delta', headerName: '+/-', width: 56 * factor, type: 'number', align: 'right', headerAlign: 'right', renderCell: DeltaCell },
            { field: 'eventsPlayed', headerName: 'Evts', width: 52 * factor, type: 'number', align: 'center', headerAlign: 'center', renderCell: WrapCell },
        ];
        return (
            <>
                <SectionHeader label='Projected' subtitle='After active events' factor={factor} />
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    <DataGridPro rows={rows} columns={cols} density='compact' getRowHeight={() => 'auto'} disableColumnMenu disableRowSelectionOnClick hideFooter={rows.length <= 100} sx={{ ...gridSx(factor), width: '100%', height: '100%' }} />
                </Box>
            </>
        );
    }

    if (view === 'pairs') {
        const refMap = new Map<string, number>();
        if (reference) {
            for (const r of reference.pairs) refMap.set(r.pairKey, r.totalPoints);
        }
        const rows = projected.pairs.map((r, i) => ({
            id: i,
            ...r,
            delta: r.totalPoints - (refMap.get(r.pairKey) ?? 0),
        }));
        const cols: GridColDef[] = [
            { field: 'rank', headerName: 'Rank', width: 52 * factor, type: 'number', align: 'center', headerAlign: 'center', renderCell: WrapCell },
            { field: 'pairName', headerName: 'Pair', flex: 1, minWidth: 80, renderCell: WrapCell },
            { field: 'totalPoints', headerName: 'Pts', width: 64 * factor, type: 'number', align: 'right', headerAlign: 'right', renderCell: WrapCell },
            { field: 'delta', headerName: '+/-', width: 56 * factor, type: 'number', align: 'right', headerAlign: 'right', renderCell: DeltaCell },
            { field: 'eventsPlayed', headerName: 'Evts', width: 52 * factor, type: 'number', align: 'center', headerAlign: 'center', renderCell: WrapCell },
        ];
        return (
            <>
                <SectionHeader label='Projected' subtitle='After active events' factor={factor} />
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    <DataGridPro rows={rows} columns={cols} density='compact' getRowHeight={() => 'auto'} disableColumnMenu disableRowSelectionOnClick hideFooter={rows.length <= 100} sx={{ ...gridSx(factor), width: '100%', height: '100%' }} />
                </Box>
            </>
        );
    }

    // teams
    const refMap = new Map<string, number>();
    if (reference) {
        for (const r of reference.teams) refMap.set(r.teamName, r.totalPoints);
    }
    const rows = projected.teams.map((r, i) => ({
        id: i,
        ...r,
        delta: r.totalPoints - (refMap.get(r.teamName) ?? 0),
    }));
    const cols: GridColDef[] = [
        { field: 'rank', headerName: 'Rank', width: 52 * factor, type: 'number', align: 'center', headerAlign: 'center', renderCell: WrapCell },
        { field: 'teamName', headerName: 'Team', flex: 1, minWidth: 80, renderCell: WrapCell },
        { field: 'totalPoints', headerName: 'Pts', width: 64 * factor, type: 'number', align: 'right', headerAlign: 'right', renderCell: WrapCell },
        { field: 'delta', headerName: '+/-', width: 56 * factor, type: 'number', align: 'right', headerAlign: 'right', renderCell: DeltaCell },
        { field: 'eventsQualified', headerName: 'Evts', width: 52 * factor, type: 'number', align: 'center', headerAlign: 'center', renderCell: WrapCell },
    ];
    return (
        <>
            <SectionHeader label='Projected' subtitle='After active events' factor={factor} />
            <Box sx={{ flex: 1, minHeight: 0 }}>
                <DataGridPro rows={rows} columns={cols} density='compact' getRowHeight={() => 'auto'} disableColumnMenu disableRowSelectionOnClick hideFooter={rows.length <= 100} sx={{ ...gridSx(factor), width: '100%', height: '100%' }} />
            </Box>
        </>
    );
}

// ─── Main dialog ──────────────────────────────────────────────────────────────

export const StandingsPreviewDialog: React.FC<StandingsPreviewDialogProps> = ({
    open,
    onClose,
    previewData,
    factor,
}) => {
    const [view, setView] = useState<'individual' | 'pairs' | 'teams'>('individual');

    const { referenceStandings, activeEventSummaries, projectedStandings } = previewData;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth='xl'
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: 'sepia.background',
                    height: '85vh',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            {/* Title row */}
            <DialogTitle sx={{ pb: 0.5 }}>
                <Stack direction='row' alignItems='center' justifyContent='space-between'>
                    <Stack direction='row' alignItems='center' spacing={1}>
                        <Typography sx={{ fontSize: 15 * factor, fontWeight: 700 }}>
                            Standings Preview
                        </Typography>
                        <Chip
                            icon={<InfoOutlinedIcon sx={{ fontSize: 12 * factor }} />}
                            label='Preview only — not saved'
                            size='small'
                            color='info'
                            variant='outlined'
                            sx={{ fontSize: 10 * factor, height: 20 * factor }}
                        />
                    </Stack>
                    <IconButton size='small' onClick={onClose}>
                        <CloseIcon sx={{ fontSize: 16 * factor }} />
                    </IconButton>
                </Stack>
            </DialogTitle>

            {/* View toggle */}
            <Box sx={{ px: 3, pb: 1 }}>
                <ToggleButtonGroup
                    value={view}
                    exclusive
                    onChange={(_, val) => { if (val) setView(val); }}
                    size='small'
                    sx={{ '& .MuiToggleButton-root': { fontSize: 11 * factor, textTransform: 'none', px: 1.5, py: 0.4 } }}
                >
                    <ToggleButton value='individual'>Individual</ToggleButton>
                    <ToggleButton value='pairs'>Pairs</ToggleButton>
                    <ToggleButton value='teams'>Teams</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Divider />

            {/* Three-column body */}
            <DialogContent sx={{ flex: 1, minHeight: 0, p: 0, display: 'flex', overflow: 'hidden' }}>
                <Stack
                    direction='row'
                    divider={<Divider orientation='vertical' flexItem />}
                    sx={{ width: '100%', height: '100%' }}
                >
                    {/* Column 1 — Reference */}
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', p: 2, overflow: 'hidden' }}>
                        <ReferenceSection standings={referenceStandings} view={view} factor={factor} />
                    </Box>

                    {/* Column 2 — Active events */}
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', p: 2, overflow: 'hidden' }}>
                        <ActiveEventsSection summaries={activeEventSummaries} view={view} factor={factor} />
                    </Box>

                    {/* Column 3 — Projected */}
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', p: 2, overflow: 'hidden' }}>
                        <ProjectedSection projected={projectedStandings} reference={referenceStandings} view={view} factor={factor} />
                    </Box>
                </Stack>
            </DialogContent>

            <Divider />

            {/* Footer — read-only note, no commit button */}
            <DialogActions sx={{ px: 3, py: 1.5 }}>
                <Typography variant='caption' sx={{ fontSize: 11 * factor, color: 'text.secondary', flex: 1 }}>
                    This preview is read-only and does not affect saved standings. Use <strong>Calculate Standings</strong> on the main view to commit.
                </Typography>
                <Button variant='outlined' size='small' onClick={onClose} sx={{ fontSize: 12 * factor, textTransform: 'none' }}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};
